import baseAPI from "@/api/base";
import {
  AccountOwnerProfile,
  AddReactionRequest,
  Attendee,
  AttendeeListResponse,
  Channel,
  Chat,
  ChatListResponse,
  CommentListResponse,
  ConnectChannelRequest,
  Conversation,
  CreateChatRequest,
  CreateDraftRequest,
  DisconnectChannelRequest,
  DraftCreatedResponse,
  EditOwnProfileRequest,
  Email,
  EmailDeletedResponse,
  EmailListResponse,
  EmailSentResponse,
  EmailUpdatedResponse,
  Folder,
  ForwardMessageRequest,
  GetAttendeeChatsRequest,
  GetAttendeeMessagesRequest,
  GetAttendeesRequest,
  GetChatMessagesRequest,
  GetCommentsRequest,
  GetConversationsRequest,
  GetEmailsRequest,
  GetFollowersRequest,
  GetFollowingRequest,
  GetInvitationsRequest,
  GetMessagesRequest,
  GetOwnProfileRequest,
  GetPostsRequest,
  GetProfileRequest,
  GetReactionsRequest,
  GetRelationsRequest,
  HandleInvitationRequest,
  InvitationCanceledResponse,
  InvitationListResponse,
  LinkedInBrowseRequest,
  LinkedInBrowseResponse,
  LinkedInImportRequest,
  LinkedInImportResponse,
  LinkedInSearchRequest,
  LinkedInSearchResponse,
  MessageListResponse,
  MessageReactionResponse,
  PerformChatActionRequest,
  PostListResponse,
  PostReactionListResponse,
  ProfileEditedResponse,
  ReconnectChannelRequest,
  SendEmailRequest,
  SendInvitationRequest,
  SendMessageToChatRequest,
  SyncChatResponse,
  UnipileMessage,
  UpdateEmailRequest,
  UserFollowersListResponse,
  UserInvitationHandledResponse,
  UserInvitationSentResponse,
  UserProfile,
  UserRelationsListResponse,
} from "@/dtos/Unipile.dto";
import {
  CANCEL_INVITATION_URL,
  CHECK_ACCOUNT_STATUS_URL,
  CONNECT_CHANNEL_URL,
  CREATE_CHAT_URL,
  CREATE_DRAFT_URL,
  ADD_MESSAGE_REACTION_URL,
  DELETE_EMAIL_URL,
  DISCONNECT_CHANNEL_URL,
  EDIT_OWN_PROFILE_URL,
  FORWARD_MESSAGE_URL,
  GET_ALL_ATTENDEES_URL,
  GET_ALL_MESSAGES_URL,
  GET_ATTENDEE_CHATS_URL,
  GET_ATTENDEE_MESSAGES_URL,
  GET_ATTENDEE_PICTURE_URL,
  GET_ATTENDEE_URL,
  GET_CHAT_ATTENDEES_URL,
  GET_COMMENTS_URL,
  GET_CONNECTED_CHANNELS_URL,
  GET_CONVERSATIONS_URL,
  GET_CHAT_BY_CONTACT_URL,
  GET_CONVERSATION_MESSAGES_URL,
  GET_CONVERSATION_URL,
  GET_EMAILS_URL,
  GET_EMAIL_ATTACHMENT_URL,
  GET_EMAIL_URL,
  GET_EMAIL_READ_RECEIPTS_URL,
  GET_FOLDERS_URL,
  GET_FOLDER_URL,
  GET_FOLLOWERS_URL,
  GET_FOLLOWING_URL,
  GET_INVITATIONS_RECEIVED_URL,
  GET_INVITATIONS_SENT_URL,
  GET_MESSAGE_ATTACHMENT_URL,
  GET_MESSAGE_URL,
  DELETE_MESSAGE_URL,
  GET_OWN_PROFILE_URL,
  GET_POSTS_URL,
  GET_PROFILE_URL,
  GET_REACTIONS_URL,
  GET_RELATIONS_URL,
  CHECK_CONNECTION_STATUS_URL,
  CHECK_CONNECTION_STATUS_BY_CONTACT_URL,
  HANDLE_INVITATION_RECEIVED_URL,
  LINKEDIN_SEARCH_URL,
  LINKEDIN_SEARCH_BROWSE_URL,
  LINKEDIN_SEARCH_IMPORT_URL,
  GET_COMPANY_PROFILE_URL,
  PATCH_CHAT_URL,
  RECONNECT_CHANNEL_URL,
  REMOVE_MESSAGE_REACTION_URL,
  SEND_EMAIL_URL,
  SEND_INVITATION_URL,
  SEND_MESSAGE_URL,
  SYNC_ALL_MESSAGES_URL,
  SYNC_CHAT_URL,
  UPDATE_EMAIL_URL,
  UPDATE_THREAD_URL,
  DELETE_THREAD_URL,
  UPLOAD_EMAIL_ATTACHMENT_URL,
} from "@/utils/unipileUrls";
import { AxiosResponse } from "axios";

class Unipile {
  /**
   * Get all connected channels/accounts
   */
  static getConnectedChannels = async (): Promise<
    AxiosResponse<{ status: boolean; data: Channel[] }>
  > => {
    const response: AxiosResponse = await baseAPI.get(GET_CONNECTED_CHANNELS_URL);
    return response;
  };

  /**
   * Initiate channel connection
   * Returns authUrl to redirect user to Unipile OAuth
   */
  static connectChannel = async (
    request: ConnectChannelRequest
  ): Promise<AxiosResponse<{ status: boolean; data: { authUrl: string; sessionId: string } }>> => {
    const response: AxiosResponse = await baseAPI.post(CONNECT_CHANNEL_URL, request);
    return response;
  };

  /**
   * Disconnect a channel
   */
  static disconnectChannel = async (
    request: DisconnectChannelRequest
  ): Promise<
    AxiosResponse<{
      status: boolean;
      data: {
        channelId: string;
        disconnected: boolean;
        message: string;
      };
    }>
  > => {
    const response: AxiosResponse = await baseAPI.post(DISCONNECT_CHANNEL_URL, request);
    return response;
  };

  /**
   * Reconnect a channel (for expired credentials)
   */
  static reconnectChannel = async (
    request: ReconnectChannelRequest
  ): Promise<AxiosResponse<{ status: boolean; data: { authUrl: string; sessionId: string } }>> => {
    const { channelId } = request;
    const url = RECONNECT_CHANNEL_URL.replace(":id", channelId);
    const response: AxiosResponse = await baseAPI.post(url);
    return response;
  };

  /**
   * Check account status manually
   */
  static checkAccountStatus = async (
    accountId: string
  ): Promise<
    AxiosResponse<{
      status: boolean;
      data: {
        accountId: string;
        unipileAccountId: string;
        oldStatus: string;
        newStatus: string;
        statusChanged: boolean;
        isActive: boolean;
        message?: string;
      };
    }>
  > => {
    const url = CHECK_ACCOUNT_STATUS_URL.replace(":accountId", accountId);
    const response: AxiosResponse = await baseAPI.post(url);
    return response;
  };

  // Chat methods
  static getConversations = async (
    request: GetConversationsRequest = {}
  ): Promise<
    AxiosResponse<{
      status: boolean;
      data: Conversation[];
      metaData?: { cursor?: string; hasMore?: boolean };
    }>
  > => {
    const params = new URLSearchParams();
    if (request.channel) params.append("channel", request.channel);
    if (request.contactId) params.append("contactId", request.contactId);
    if (request.search) params.append("search", request.search);
    if (request.limit) params.append("limit", request.limit.toString());
    if (request.cursor) params.append("cursor", request.cursor);
    if (request.before) params.append("before", request.before);
    if (request.after) params.append("after", request.after);
    if (request.unread !== undefined) params.append("unread", request.unread.toString());
    if (request.account_type) params.append("account_type", request.account_type);
    if (request.account_id) params.append("account_id", request.account_id);

    const url = GET_CONVERSATIONS_URL + (params.toString() ? `?${params.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Get chat by contact ID - optimized endpoint for LinkedIn
   * Returns chat with participants and initial messages in one request
   */
  static getChatByContact = async (
    contactId: string,
    channel: string = "linkedin",
    accountId?: string
  ): Promise<
    AxiosResponse<{
      status: boolean;
      data: {
        chat: Conversation | null;
        chats?: Conversation[];
        selectedChat?: Conversation | null;
        messages: UnipileMessage[];
        hasMore: boolean;
        oldestTimestamp?: string | null;
        isConnected?: boolean;
        canSendInMail?: boolean;
        isInMail?: boolean;
        providerId?: string | null;
        pendingInMails?: any[];
        pendingConnectionNotes?: any[];
        invitationStatus?: {
          type: "SENT";
          status: "PENDING" | "ACCEPTED" | "EXPIRED" | "CANCELLED";
        } | null;
        inmailBalance?: any;
      };
    }>
  > => {
    const params = new URLSearchParams();
    params.append("channel", channel);
    if (accountId) params.append("account_id", accountId);

    const url = `${GET_CHAT_BY_CONTACT_URL}/${contactId}?${params.toString()}`;
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Get a single chat by ID
   */
  static getChat = async (
    chatId: string,
    accountId?: string
  ): Promise<AxiosResponse<{ status: boolean; data: Chat }>> => {
    const params = new URLSearchParams();
    if (accountId) params.append("account_id", accountId);
    const url =
      GET_CONVERSATION_URL.replace(":id", chatId) +
      (params.toString() ? `?${params.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Get messages for a specific chat
   */
  static getConversationMessages = async (
    conversationId: string,
    params?: GetChatMessagesRequest
  ): Promise<AxiosResponse<{ status: boolean; data: MessageListResponse }>> => {
    const queryParams = new URLSearchParams();
    if (params?.cursor) queryParams.append("cursor", params.cursor);
    if (params?.before) queryParams.append("before", params.before);
    if (params?.after) queryParams.append("after", params.after);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.sender_id) queryParams.append("sender_id", params.sender_id);

    const url =
      GET_CONVERSATION_MESSAGES_URL.replace(":id", conversationId) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Send a message to an existing chat
   */
  static sendMessage = async (
    chatId: string,
    request: SendMessageToChatRequest
  ): Promise<AxiosResponse<{ status: boolean; data: { object: string; message_id: string } }>> => {
    const url = SEND_MESSAGE_URL.replace(":id", chatId);

    const hasAttachments =
      request.attachments &&
      Array.isArray(request.attachments) &&
      request.attachments.length > 0 &&
      request.attachments.some((item) => item instanceof File);
    const hasVoiceMessage = request.voice_message && request.voice_message instanceof File;
    const hasVideoMessage = request.video_message && request.video_message instanceof File;
    const hasFiles = hasAttachments || hasVoiceMessage || hasVideoMessage;

    if (hasFiles) {
      const formData = new FormData();

      if (request.text) {
        formData.append("text", request.text);
      }

      if (request.quote_id) {
        formData.append("quote_id", request.quote_id);
      }

      if (request.typing_duration) {
        formData.append("typing_duration", request.typing_duration);
      }

      if (hasAttachments && request.attachments) {
        request.attachments.forEach((file) => {
          if (file instanceof File) {
            formData.append("attachments", file);
          }
        });
      }

      if (hasVoiceMessage) {
        formData.append("voice_message", request.voice_message);
      }

      if (hasVideoMessage) {
        formData.append("video_message", request.video_message);
      }

      const response: AxiosResponse = await baseAPI.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } else {
      const jsonPayload: any = {};
      if (request.text) {
        jsonPayload.text = request.text;
      }
      if (request.quote_id) {
        jsonPayload.quote_id = request.quote_id;
      }
      if (request.typing_duration) {
        jsonPayload.typing_duration = request.typing_duration;
      }
      const response: AxiosResponse = await baseAPI.post(url, jsonPayload);
      return response;
    }
  };

  /**
   * Create a new chat (1-to-1 or group)
   */
  static createGroupChat = async (
    request: CreateChatRequest
  ): Promise<
    AxiosResponse<{ status: boolean; data: Conversation & { chat_id: string; message_id: string } }>
  > => {
    const hasFiles =
      (request.attachments &&
        request.attachments.length > 0 &&
        request.attachments[0] instanceof File) ||
      (request.voice_message && request.voice_message instanceof File) ||
      (request.video_message && request.video_message instanceof File);

    if (hasFiles) {
      const formData = new FormData();
      formData.append("account_id", request.account_id);

      request.attendees_ids.forEach((id) => {
        formData.append("attendees_ids", id);
      });

      if (request.text) formData.append("text", request.text);
      if (request.title) formData.append("title", request.title);
      if (request.subject) formData.append("subject", request.subject);

      if (request.attachments && request.attachments.length > 0) {
        request.attachments.forEach((file) => {
          if (file instanceof File) {
            formData.append("attachments", file);
          }
        });
      }

      if (request.voice_message && request.voice_message instanceof File) {
        formData.append("voice_message", request.voice_message);
      }

      if (request.video_message && request.video_message instanceof File) {
        formData.append("video_message", request.video_message);
      }

      if (request.linkedin) {
        // Format linkedin params as linkedin[...]
        Object.keys(request.linkedin).forEach((key) => {
          const value = request.linkedin![key];
          if (value !== undefined && value !== null) {
            if (typeof value === "boolean") {
              formData.append(`linkedin[${key}]`, value.toString());
            } else if (typeof value === "string") {
              formData.append(`linkedin[${key}]`, value);
            } else {
              formData.append(`linkedin[${key}]`, JSON.stringify(value));
            }
          }
        });
      }

      const response: AxiosResponse = await baseAPI.post(CREATE_CHAT_URL, formData);
      return response;
    } else {
      const response: AxiosResponse = await baseAPI.post(CREATE_CHAT_URL, request);
      return response;
    }
  };

  /**
   * Perform an action on a chat (setReadStatus, setMuteStatus, etc.)
   */
  static performChatAction = async (
    chatId: string,
    request: PerformChatActionRequest
  ): Promise<AxiosResponse<{ status: boolean; data: { object: string } }>> => {
    const url = PATCH_CHAT_URL.replace(":id", chatId);
    const response: AxiosResponse = await baseAPI.patch(url, request);
    return response;
  };

  /**
   * Get all attendees from a chat
   */
  static getChatAttendees = async (
    chatId: string,
    accountId?: string
  ): Promise<AxiosResponse<{ status: boolean; data: AttendeeListResponse }>> => {
    const params = new URLSearchParams();
    if (accountId) params.append("account_id", accountId);
    const url =
      GET_CHAT_ATTENDEES_URL.replace(":id", chatId) +
      (params.toString() ? `?${params.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Synchronize a conversation from its beginning
   */
  static syncChat = async (
    conversationId: string
  ): Promise<AxiosResponse<{ status: boolean; data: SyncChatResponse }>> => {
    const url = SYNC_CHAT_URL.replace(":id", conversationId);
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Sync all messages for an account
   */
  static syncAllMessages = async (
    accountId: string,
    since?: string
  ): Promise<
    AxiosResponse<{ status: boolean; data: { message: string; syncedCount: number } }>
  > => {
    const params = new URLSearchParams();
    params.append("accountId", accountId);
    if (since) params.append("since", since);
    const url = SYNC_ALL_MESSAGES_URL + `?${params.toString()}`;
    const response: AxiosResponse = await baseAPI.post(url);
    return response;
  };

  /**
   * List all messages
   */
  static getAllMessages = async (
    params?: GetMessagesRequest
  ): Promise<AxiosResponse<{ status: boolean; data: MessageListResponse }>> => {
    const queryParams = new URLSearchParams();
    if (params?.cursor) queryParams.append("cursor", params.cursor);
    if (params?.before) queryParams.append("before", params.before);
    if (params?.after) queryParams.append("after", params.after);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.sender_id) queryParams.append("sender_id", params.sender_id);
    if (params?.account_id) queryParams.append("account_id", params.account_id);

    const url = GET_ALL_MESSAGES_URL + (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Retrieve a single message by ID
   */
  static getMessage = async (
    messageId: string
  ): Promise<AxiosResponse<{ status: boolean; data: UnipileMessage }>> => {
    const url = GET_MESSAGE_URL.replace(":id", messageId);
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Forward a message to a chat (WhatsApp only)
   */
  static forwardMessage = async (
    messageId: string,
    request: ForwardMessageRequest
  ): Promise<AxiosResponse<{ status: boolean; data: { object: string; message_id: string } }>> => {
    const url = FORWARD_MESSAGE_URL.replace(":id", messageId);
    const response: AxiosResponse = await baseAPI.post(url, request);
    return response;
  };

  /**
   * Add (or replace) the current user's reaction on a message
   */
  static addReaction = async (
    messageId: string,
    request: AddReactionRequest
  ): Promise<AxiosResponse<{ status: boolean; data: MessageReactionResponse }>> => {
    const url = ADD_MESSAGE_REACTION_URL.replace(":id", messageId);
    const response: AxiosResponse = await baseAPI.post(url, request);
    return response;
  };

  /**
   * Remove the current user's reaction from a message
   */
  static removeReaction = async (
    messageId: string
  ): Promise<AxiosResponse<{ status: boolean; data: MessageReactionResponse }>> => {
    const url = REMOVE_MESSAGE_REACTION_URL.replace(":id", messageId);
    const response: AxiosResponse = await baseAPI.delete(url);
    return response;
  };

  /**
   * Download an attachment from a message
   */
  static getMessageAttachment = async (
    messageId: string,
    attachmentId: string,
    onProgress?: (progress: number, loaded: number, total: number) => void,
    signal?: AbortSignal
  ): Promise<AxiosResponse<Blob>> => {
    const url = GET_MESSAGE_ATTACHMENT_URL.replace(":message_id", messageId).replace(
      ":attachment_id",
      attachmentId
    );
    const response: AxiosResponse = await baseAPI.get(url, {
      responseType: "blob",
      timeout: 0,
      signal,
      onDownloadProgress: (progressEvent) => {
        if (onProgress) {
          const total = progressEvent.total || 0;
          const progress = total ? (progressEvent.loaded / total) * 100 : 0;
          onProgress(progress, progressEvent.loaded, total);
        }
      },
    });
    return response;
  };

  /**
   * List all attendees (across all chats)
   */
  static getAllAttendees = async (
    params?: GetAttendeesRequest
  ): Promise<AxiosResponse<{ status: boolean; data: AttendeeListResponse }>> => {
    const queryParams = new URLSearchParams();
    if (params?.cursor) queryParams.append("cursor", params.cursor);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.account_id) queryParams.append("account_id", params.account_id);

    const url =
      GET_ALL_ATTENDEES_URL + (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Retrieve a single attendee by ID
   */
  static getAttendee = async (
    attendeeId: string
  ): Promise<AxiosResponse<{ status: boolean; data: Attendee }>> => {
    const url = GET_ATTENDEE_URL.replace(":id", attendeeId);
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Download a chat attendee picture
   */
  static getAttendeePicture = async (id: string): Promise<AxiosResponse<Blob>> => {
    const url = GET_ATTENDEE_PICTURE_URL.replace(":id", id);
    const response: AxiosResponse = await baseAPI.get(url, { responseType: "blob" });
    return response;
  };

  /**
   * List all 1to1 chats for a given attendee
   */
  static getAttendeeChats = async (
    attendeeId: string,
    params?: GetAttendeeChatsRequest
  ): Promise<AxiosResponse<{ status: boolean; data: ChatListResponse }>> => {
    const queryParams = new URLSearchParams();
    if (params?.cursor) queryParams.append("cursor", params.cursor);
    if (params?.before) queryParams.append("before", params.before);
    if (params?.after) queryParams.append("after", params.after);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.account_id) queryParams.append("account_id", params.account_id);
    if (params?.contact_id) queryParams.append("contact_id", params.contact_id);

    const url =
      GET_ATTENDEE_CHATS_URL.replace(":attendee_id", attendeeId) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * List all messages for a given attendee
   */
  static getAttendeeMessages = async (
    senderId: string,
    params?: GetAttendeeMessagesRequest
  ): Promise<AxiosResponse<{ status: boolean; data: MessageListResponse }>> => {
    const queryParams = new URLSearchParams();
    if (params?.cursor) queryParams.append("cursor", params.cursor);
    if (params?.before) queryParams.append("before", params.before);
    if (params?.after) queryParams.append("after", params.after);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.account_id) queryParams.append("account_id", params.account_id);

    const url =
      GET_ATTENDEE_MESSAGES_URL.replace(":sender_id", senderId) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * List all emails
   */
  static getAllEmails = async (
    params: GetEmailsRequest
  ): Promise<AxiosResponse<{ status: boolean; data: EmailListResponse }>> => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", params.account_id);
    if (params.message_id) queryParams.append("message_id", params.message_id);
    if (params.thread_id) queryParams.append("thread_id", params.thread_id);
    if (params.meta_only !== undefined)
      queryParams.append("meta_only", params.meta_only.toString());
    if (params.include_headers !== undefined)
      queryParams.append("include_headers", params.include_headers.toString());
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.before) queryParams.append("before", params.before);
    if (params.after) queryParams.append("after", params.after);
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.any_email) queryParams.append("any_email", params.any_email);
    if (params.to) queryParams.append("to", params.to);
    if (params.from) queryParams.append("from", params.from);
    if (params.exclude_folders) queryParams.append("exclude_folders", params.exclude_folders);
    if (params.folder) queryParams.append("folder", params.folder);

    const url = GET_EMAILS_URL + `?${queryParams.toString()}`;
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Send an email
   * Automatically uses FormData when attachments (File objects) are present
   */
  static sendEmail = async (
    request: SendEmailRequest
  ): Promise<AxiosResponse<{ status: boolean; data: EmailSentResponse }>> => {
    // Check if request has File objects as attachments
    const hasFileAttachments =
      request.attachments &&
      Array.isArray(request.attachments) &&
      request.attachments.length > 0 &&
      request.attachments.some((att) => att instanceof File);

    if (hasFileAttachments) {
      // Create FormData for multipart/form-data upload
      const formData = new FormData();
      formData.append("account_id", request.account_id);
      formData.append("body", request.body);
      formData.append("to", JSON.stringify(request.to));

      if (request.from) formData.append("from", JSON.stringify(request.from));
      if (request.cc && Array.isArray(request.cc))
        formData.append("cc", JSON.stringify(request.cc));
      if (request.bcc && Array.isArray(request.bcc))
        formData.append("bcc", JSON.stringify(request.bcc));
      if (request.subject) formData.append("subject", request.subject);
      if (request.reply_to) formData.append("reply_to", request.reply_to);
      if (request.custom_headers && Array.isArray(request.custom_headers))
        formData.append("custom_headers", JSON.stringify(request.custom_headers));
      if (request.tracking_options && typeof request.tracking_options === "object")
        formData.append("tracking_options", JSON.stringify(request.tracking_options));

      // Append File objects directly
      if (request.attachments && Array.isArray(request.attachments)) {
        request.attachments.forEach((file) => {
          if (file instanceof File) {
            formData.append("attachments", file);
          }
        });
      }

      const response: AxiosResponse = await baseAPI.post(SEND_EMAIL_URL, formData);
      return response;
    } else {
      // Send as JSON when no File attachments
      const response: AxiosResponse = await baseAPI.post(SEND_EMAIL_URL, request);
      return response;
    }
  };

  /**
   * Retrieve an email by ID
   */
  static getEmail = async (
    emailId: string,
    accountId?: string,
    includeHeaders?: boolean
  ): Promise<AxiosResponse<{ status: boolean; data: Email }>> => {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append("account_id", accountId);
    if (includeHeaders !== undefined)
      queryParams.append("include_headers", includeHeaders.toString());

    const url =
      GET_EMAIL_URL.replace(":id", emailId) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Delete an email
   */
  static deleteEmail = async (
    emailId: string,
    accountId?: string
  ): Promise<AxiosResponse<{ status: boolean; data: EmailDeletedResponse }>> => {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append("account_id", accountId);

    const url =
      DELETE_EMAIL_URL.replace(":id", emailId) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.delete(url);
    return response;
  };

  /**
   * Delete a message (WhatsApp/LinkedIn)
   */
  static deleteMessage = async (messageId: string) => {
    const url = DELETE_MESSAGE_URL.replace(":id", messageId);
    return baseAPI.delete(url);
  };

  /**
   * Update an email (mark as read/unread, move to folders, etc.)
   */
  static updateEmail = async (
    emailId: string,
    request: UpdateEmailRequest,
    accountId?: string
  ): Promise<AxiosResponse<{ status: boolean; data: EmailUpdatedResponse }>> => {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append("account_id", accountId);

    const url =
      UPDATE_EMAIL_URL.replace(":id", emailId) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.put(url, request);
    return response;
  };

  /**
   * Update all emails in a thread (mark as read/unread)
   */
  static updateThread = async (
    threadId: string,
    request: { unread: boolean },
    accountId: string
  ): Promise<
    AxiosResponse<{
      status: boolean;
      data: { object: string; thread_id: string; updated_count: number; unread: boolean };
    }>
  > => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", accountId);

    const url =
      UPDATE_THREAD_URL.replace(":thread_id", threadId) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.put(url, request);
    return response;
  };

  /**
   * Delete all emails in a thread
   */
  static deleteThread = async (
    threadId: string,
    accountId: string
  ): Promise<
    AxiosResponse<{
      status: boolean;
      data: { object: string; thread_id: string; deleted_count: number };
    }>
  > => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", accountId);

    const url =
      DELETE_THREAD_URL.replace(":thread_id", threadId) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.delete(url);
    return response;
  };

  /**
   * Retrieve an attachment from an email
   */
  static getEmailAttachment = async (
    emailId: string,
    attachmentId: string
  ): Promise<AxiosResponse<Blob>> => {
    const url = GET_EMAIL_ATTACHMENT_URL.replace(":email_id", emailId).replace(
      ":attachment_id",
      attachmentId
    );
    const response: AxiosResponse = await baseAPI.get(url, { responseType: "blob" });
    return response;
  };

  /**
   * Get read receipts for an email
   */
  static getEmailReadReceipts = async (
    emailId: string,
    altId?: string | null
  ): Promise<
    AxiosResponse<{
      object: "EmailReadReceipts";
      emailId: string;
      readCount: number;
      readReceipts: Array<{
        id: string;
        readAt: string;
        recipientEmail: string;
        contactId: string;
        accountId: string;
        ip: string | null;
        userAgent: string | null;
      }>;
    }>
  > => {
    const url = GET_EMAIL_READ_RECEIPTS_URL.replace(":email_id", emailId);
    const params: Record<string, string> = {};
    if (altId) params.alt_id = altId;
    const response: AxiosResponse = await baseAPI.get(url, { params });
    return response;
  };

  /**
   * Get pre-signed URL for email attachment upload
   */
  static getPresignedAttachmentUploadUrl = async (data: {
    fileName: string;
    fileType: string;
    fileSize?: number;
  }): Promise<
    AxiosResponse<{
      status: boolean;
      data: {
        uploadUrl: string;
        publicUrl: string;
        key: string;
      };
    }>
  > => {
    const response: AxiosResponse = await baseAPI.post(UPLOAD_EMAIL_ATTACHMENT_URL, data);
    return response;
  };

  /**
   * List all folders
   */
  static getAllFolders = async (
    accountId?: string
  ): Promise<AxiosResponse<{ status: boolean; data: Folder[] }>> => {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append("account_id", accountId);

    const url = GET_FOLDERS_URL + (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Retrieve a folder by ID
   */
  static getFolder = async (
    folderId: string,
    accountId?: string
  ): Promise<AxiosResponse<{ status: boolean; data: Folder }>> => {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append("account_id", accountId);

    const url =
      GET_FOLDER_URL.replace(":id", folderId) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Create a draft
   */
  static createDraft = async (
    request: CreateDraftRequest
  ): Promise<AxiosResponse<{ status: boolean; data: DraftCreatedResponse }>> => {
    const response: AxiosResponse = await baseAPI.post(CREATE_DRAFT_URL, request);
    return response;
  };

  /**
   * List all invitations sent
   */
  static getAllInvitationsSent = async (
    params: GetInvitationsRequest
  ): Promise<AxiosResponse<{ status: boolean; data: InvitationListResponse }>> => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", params.account_id);
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());

    const url =
      GET_INVITATIONS_SENT_URL + (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * List all invitations received
   */
  static getAllInvitationsReceived = async (
    params: GetInvitationsRequest
  ): Promise<AxiosResponse<{ status: boolean; data: InvitationListResponse }>> => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", params.account_id);
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());

    const url =
      GET_INVITATIONS_RECEIVED_URL + (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Handle a received invitation
   */
  static handleInvitationReceived = async (
    invitationId: string,
    request: HandleInvitationRequest
  ): Promise<AxiosResponse<{ status: boolean; data: UserInvitationHandledResponse }>> => {
    const url = HANDLE_INVITATION_RECEIVED_URL.replace(":invitation_id", invitationId);
    const response: AxiosResponse = await baseAPI.post(url, request);
    return response;
  };

  /**
   * Retrieve own profile
   */
  static getOwnProfile = async (
    params: GetOwnProfileRequest = {}
  ): Promise<AxiosResponse<{ status: boolean; data: AccountOwnerProfile }>> => {
    const queryParams = new URLSearchParams();
    if (params.account_id) queryParams.append("account_id", params.account_id);

    const url = GET_OWN_PROFILE_URL + (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Edit own profile
   */
  static editOwnProfile = async (
    request: EditOwnProfileRequest
  ): Promise<AxiosResponse<{ status: boolean; data: ProfileEditedResponse }>> => {
    const response: AxiosResponse = await baseAPI.patch(EDIT_OWN_PROFILE_URL, request);
    return response;
  };

  /**
   * List all relations
   */
  static getAllRelations = async (
    params: GetRelationsRequest
  ): Promise<AxiosResponse<{ status: boolean; data: UserRelationsListResponse }>> => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", params.account_id);
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.filter) queryParams.append("filter", params.filter);
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());

    const url = GET_RELATIONS_URL + (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Check if a LinkedIn contact is already connected
   */
  static checkConnectionStatus = async (
    accountId: string,
    linkedinIdentifier: string
  ): Promise<AxiosResponse<{ status: boolean; data: { isConnected: boolean } }>> => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", accountId);
    queryParams.append("linkedin_identifier", linkedinIdentifier);

    const url = CHECK_CONNECTION_STATUS_URL + `?${queryParams.toString()}`;
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Get InMail credit balance for LinkedIn account
   */
  static getInmailBalance = async (
    accountId: string
  ): Promise<
    AxiosResponse<{
      status: boolean;
      data: {
        object: string;
        premium: number;
        recruiter: number;
        sales_navigator: number;
      };
    }>
  > => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", accountId);

    const url = `/unipile/linkedin/inmail_balance?${queryParams.toString()}`;
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Check if a LinkedIn contact is already connected (by contactId)
   */
  static checkLinkedInConnection = async (
    contactId: string,
    accountId: string
  ): Promise<AxiosResponse<{ status: boolean; data: { isConnected: boolean } }>> => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", accountId);
    queryParams.append("contactId", contactId);

    const url = CHECK_CONNECTION_STATUS_BY_CONTACT_URL + `?${queryParams.toString()}`;
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * List all followed accounts
   */
  static getAllFollowing = async (
    params: GetFollowingRequest
  ): Promise<AxiosResponse<{ status: boolean; data: UserFollowersListResponse }>> => {
    const queryParams = new URLSearchParams();
    if (params.account_id) queryParams.append("account_id", params.account_id);
    if (params.user_id) queryParams.append("user_id", params.user_id);
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());

    const url = GET_FOLLOWING_URL + (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * List all followers
   */
  static getAllFollowers = async (
    params: GetFollowersRequest
  ): Promise<AxiosResponse<{ status: boolean; data: UserFollowersListResponse }>> => {
    const queryParams = new URLSearchParams();
    if (params.account_id) queryParams.append("account_id", params.account_id);
    if (params.user_id) queryParams.append("user_id", params.user_id);
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());

    const url = GET_FOLLOWERS_URL + (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Retrieve a profile
   */
  static getProfile = async (
    identifier: string,
    params: GetProfileRequest = {}
  ): Promise<AxiosResponse<{ status: boolean; data: UserProfile }>> => {
    const queryParams = new URLSearchParams();
    if (params.account_id) queryParams.append("account_id", params.account_id);
    if (params.linkedin_sections) {
      if (Array.isArray(params.linkedin_sections)) {
        queryParams.append("linkedin_sections", params.linkedin_sections.join(","));
      } else {
        queryParams.append("linkedin_sections", params.linkedin_sections);
      }
    }
    if (params.linkedin_api) queryParams.append("linkedin_api", params.linkedin_api);
    if (params.notify !== undefined) queryParams.append("notify", params.notify.toString());

    const url =
      GET_PROFILE_URL.replace(":identifier", identifier) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Retrieve a LinkedIn company profile
   * GET /api/v1/unipile/linkedin/company/:identifier
   * Path: identifier (public identifier, ID, or URN)
   * Query: account_id (required)
   */
  static getCompanyProfile = async (
    identifier: string,
    params: { account_id: string }
  ): Promise<AxiosResponse<{ status: boolean; data: any }>> => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", params.account_id);

    const url =
      GET_COMPANY_PROFILE_URL.replace(":identifier", encodeURIComponent(identifier)) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Send an invitation
   */
  static sendInvitation = async (
    request: SendInvitationRequest
  ): Promise<AxiosResponse<{ status: boolean; data: UserInvitationSentResponse }>> => {
    const response: AxiosResponse = await baseAPI.post(SEND_INVITATION_URL, request);
    return response;
  };

  /**
   * Send a LinkedIn connection request
   */
  static sendConnectionRequest = async (
    identifier: string,
    options: { account_id: string; message?: string; contactId?: string }
  ): Promise<AxiosResponse<{ status: boolean; data: UserInvitationSentResponse }>> => {
    const request: SendInvitationRequest = {
      provider_id: identifier,
      account_id: options.account_id,
      message: options.message,
      contactId: options.contactId,
    };
    return await Unipile.sendInvitation(request);
  };

  /**
   * List all posts
   */
  static getAllPosts = async (
    identifier: string,
    params: GetPostsRequest = {}
  ): Promise<AxiosResponse<{ status: boolean; data: PostListResponse }>> => {
    const queryParams = new URLSearchParams();
    if (params.account_id) queryParams.append("account_id", params.account_id);
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
    if (params.is_company !== undefined)
      queryParams.append("is_company", params.is_company.toString());

    const url =
      GET_POSTS_URL.replace(":identifier", identifier) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * List all comments
   */
  static getAllComments = async (
    identifier: string,
    params: GetCommentsRequest = {}
  ): Promise<AxiosResponse<{ status: boolean; data: CommentListResponse }>> => {
    const queryParams = new URLSearchParams();
    if (params.account_id) queryParams.append("account_id", params.account_id);
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());

    const url =
      GET_COMMENTS_URL.replace(":identifier", identifier) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * List all reactions
   */
  static getAllReactions = async (
    identifier: string,
    params: GetReactionsRequest = {}
  ): Promise<AxiosResponse<{ status: boolean; data: PostReactionListResponse }>> => {
    const queryParams = new URLSearchParams();
    if (params.account_id) queryParams.append("account_id", params.account_id);
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());

    const url =
      GET_REACTIONS_URL.replace(":identifier", identifier) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.get(url);
    return response;
  };

  /**
   * Cancel an invitation
   */
  static cancelInvitation = async (
    invitationId: string,
    accountId: string
  ): Promise<AxiosResponse<{ status: boolean; data: InvitationCanceledResponse }>> => {
    const queryParams = new URLSearchParams();
    queryParams.append("account_id", accountId);

    const url =
      CANCEL_INVITATION_URL.replace(":invitation_id", invitationId) +
      (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.delete(url);
    return response;
  };

  /**
   * Search LinkedIn Sales Navigator
   */
  static searchLinkedIn = async (
    request: LinkedInSearchRequest
  ): Promise<AxiosResponse<{ status: boolean; data: LinkedInSearchResponse }>> => {
    const queryParams = new URLSearchParams();
    if (request.account_id) {
      queryParams.append("account_id", request.account_id);
    }
    if (request.limit !== undefined) {
      queryParams.append("limit", request.limit.toString());
    }
    if (request.cursor) {
      queryParams.append("cursor", request.cursor);
    }

    const url = LINKEDIN_SEARCH_URL + (queryParams.toString() ? `?${queryParams.toString()}` : "");
    const response: AxiosResponse = await baseAPI.post(url, {
      url: request.url,
      taskType: request.taskType,
      bucketOptionId: request.bucketOptionId,
      followUpDate: request.followUpDate,
      enrichEmail: request.enrichEmail,
      enrichPhoneNumber: request.enrichPhoneNumber,
      selectedContactIds: request.selectedContactIds,
    });
    return response;
  };

  /**
   * Browse a Sales Navigator search page-by-page. Server stores a
   * snapshot keyed by sessionId so /import can resolve selectedProspectIds
   * without re-running the search.
   */
  static browseLinkedInSearch = async (
    request: LinkedInBrowseRequest
  ): Promise<AxiosResponse<{ status: boolean; data: LinkedInBrowseResponse }>> => {
    const response: AxiosResponse = await baseAPI.post(LINKEDIN_SEARCH_BROWSE_URL, request);
    return response;
  };

  /**
   * Fire-and-forget bulk LinkedIn import. Server returns 202 immediately;
   * dispatches enrichments in the background and notifies on completion.
   */
  static startLinkedInImport = async (
    request: LinkedInImportRequest
  ): Promise<AxiosResponse<{ status: boolean; data: LinkedInImportResponse; msg?: string }>> => {
    const response: AxiosResponse = await baseAPI.post(LINKEDIN_SEARCH_IMPORT_URL, request);
    return response;
  };
}

export default Unipile;
